import {formatCurrency, useDateTimeFormatter, useFormatNumber, useLocale} from 'platform/locale';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import type {GridPdfExportCallback} from 'features/eag-grid';

import type {WarehouseGridPdfDefinition} from '../types/WarehouseGridPdfDefinition';
import {createWarehouseGridPdfRecords} from '../utils/createWarehouseGridPdfRecords';

const BRAND = 'OMNETIC';

export function useWarehouseGridPdfExport(
  title: string,
  definition: WarehouseGridPdfDefinition
): GridPdfExportCallback {
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();
  const {localeConfig} = useLocale();

  return async (context, signal) => {
    signal.throwIfAborted();
    const records = createWarehouseGridPdfRecords(context, definition, {
      date: (value) => formatDateTime('dateShort', value),
      dateTime: (value) => formatDateTime('dateTimeShort', value),
      number: (value) => formatNumber(value),
      currency: (amount, currency) => {
        const formatted = formatCurrency(localeConfig, amount, 2);
        return isNil(formatted) ? null : `${formatted} ${currency}`;
      },
    });
    const selection = i18n.t('page.datagrid.labels.numberOfSelectedRows', {
      selectedRows: records.length,
    });
    const exportedAt = formatDateTime('dateTimeShort', context.exportedAt);
    const {createWarehouseGridPdf} = await import('../utils/createWarehouseGridPdf');
    signal.throwIfAborted();
    const bytes = await createWarehouseGridPdf(
      {
        title,
        brand: BRAND,
        metadata: `${selection} | ${i18n.t('general.actions.export')} ${exportedAt}`,
        columnLayout: {count: 4, headerAlign: 'left'},
        records,
      },
      signal
    );
    return {bytes, filename: definition.filename};
  };
}
